
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import {TableItemFormatter} from '../../elements/charts/helpers/tableItemFormatter';
import settingx from '@/store/modules/settingx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import { COL, sqlSumByDocType, sqlSum, getTableName } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        TableFilterable,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class AgentTopAgentTable extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = [];

    public tableItems: any[] = [];
    public tableFields: any[] = [];

    public get permissionIds(): PermissionsGroup[] {
        return ['sales'];
    }

    public get exportFileName() {
        return 'Top Agent' + '_' + this.monthName + ' ' + this.yearName;
    }

    public get currentMonth() {
        return moment(this.selectedAsOfDate).month();
    }

    public get currentYear() {
        return moment(this.selectedAsOfDate).year();
    }

    public get monthName() {
        return moment().month(this.currentMonth).format('MMM').toUpperCase();
    }

    public get yearName() {
        return moment().year(this.currentYear).format('YY').toUpperCase();
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedAgents,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedAgents,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        this.generateLoadingText(0.2, 0.3);

        // Build filter clauses manually for cross-table UNION
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

        let agentFilter = '';
        if (this.selectedAgents.length > 0) {
            const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
            agentFilter = ` AND ${COL.AGENT} IN (${list})`;
        }

        let docProjectFilter = '';
        if (this.selectedDocProjects.length > 0) {
            const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            docProjectFilter = ` AND "DOCPROJECT" IN (${list})`;
        }

        let dtlProjectFilter = '';
        if (this.selectedDTLProjects.length > 0) {
            const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            dtlProjectFilter = ` AND "DTLPROJECT" IN (${list})`;
        }

        const baseFilter = `${dateFilter}${agentFilter}${docProjectFilter}${dtlProjectFilter}`;

        // Get table names
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        // Get a head for executing queries (ensures all tables are loaded)
        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded([
            'sales', 'creditNotes', 'customerTrades',
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Build agent data map: { agent: { ivCs, cn, dn } }
        const agentMap: Map<string, { ivCs: number; cn: number; dn: number }> = new Map();

        if (this.slPhModuleOnly) {
            // SL Mode - single UNION query combining sales and creditNotes
            const data = await queryHead.rawQuery(`
                SELECT agent,
                    SUM(CASE WHEN docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amt,
                    SUM(CASE WHEN docType = 'DN' THEN amount ELSE 0 END) as dn_amt,
                    SUM(CASE WHEN docType = 'CN' THEN amount ELSE 0 END) as cn_amt
                FROM (
                    SELECT ${COL.AGENT} as agent, ${COL.DOCTYPE} as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                    FROM ${salesTable}
                    WHERE ${baseFilter}
                    UNION ALL
                    SELECT ${COL.AGENT} as agent, 'CN' as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                    FROM ${cnTable}
                    WHERE ${baseFilter}
                ) AS combined
                GROUP BY agent
            `);
            this.generateLoadingText(0.95);

            for (const row of data) {
                const agent = String(row.agent || '');
                agentMap.set(agent, {
                    ivCs: Number(row.iv_cs_amt) || 0,
                    cn: Number(row.cn_amt) || 0,
                    dn: Number(row.dn_amt) || 0,
                });
            }
        } else {
            // AR Mode - single query on customerTrades
            const data = await queryHead.rawQuery(`
                SELECT ${COL.AGENT} as agent,
                    ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amt')},
                    ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amt')},
                    ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amt')}
                FROM ${arTable}
                WHERE ${baseFilter}
                GROUP BY ${COL.AGENT}
            `);
            this.generateLoadingText(0.95);

            for (const row of data) {
                const agent = String(row.agent || '');
                agentMap.set(agent, {
                    ivCs: Number(row.iv_amt) || 0,
                    cn: Number(row.cn_amt) || 0,
                    dn: Number(row.dn_amt) || 0,
                });
            }
        }

        // Calculate totals and build final array
        let totalIvCs = 0;
        let totalCn = 0;
        let totalDn = 0;

        const finalArray: Array<{ agent: string; sales: number; percentage: number }> = [];

        for (const [agent, data] of agentMap) {
            totalIvCs += data.ivCs;
            totalCn += data.cn;
            totalDn += data.dn;

            const sales = data.ivCs
                - (this.includeCreditNote ? data.cn : 0)
                + (this.includeDebitNote ? data.dn : 0);

            finalArray.push({ agent, sales, percentage: 0 });
        }

        const totalSalesAmount = totalIvCs
            - (this.includeCreditNote ? totalCn : 0)
            + (this.includeDebitNote ? totalDn : 0);

        // Sort by sales descending
        finalArray.sort((a, b) => b.sales - a.sales);

        // Build result: top 5 + remaining
        let result: Array<{ agent: string; sales: number; percentage: number }> = [];

        for (let i = 0; i < Math.min(5, finalArray.length); i++) {
            result.push(finalArray[i]);
        }

        if (finalArray.length > 5) {
            const remainingSales = finalArray.slice(5).reduce((sum, a) => sum + a.sales, 0);
            result.push({
                agent: 'REMAINING AGENTS',
                sales: remainingSales,
                percentage: 0,
            });
        }

        // Calculate percentages
        result = result.map((a) => ({
            ...a,
            percentage: totalSalesAmount ? (a.sales / totalSalesAmount) * 100 : 0,
        }));

        this.tableFields = [
            {key: 'agent', sortable: false},
            {
                key: 'sales',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'percentage',
                sortable: false,
                formatter: TableItemFormatter.percentage,
            },
        ];

        this.tableItems = result;

        this.generateLoadingText(1);
        this.saveHistory('tableItems', 'tableFields');
    }
}
